import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';

import { SupportTicketsService } from '@shared/services/entities/supportTickets.service';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { ModulesService } from '@shared/services/modules.service';
import { MailService } from '@shared/services/mail.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';

import { Role } from '@shared/models/entities/roles.model';
import { SupportTicket, TicketStatus, TICKET_STATUSES, ticketPriority } from '@shared/models/entities/supportTicket.model';
import { isEpioneAdmin } from '@shared/utils/epione-admin';
import { sortAlphabetically } from '@shared/utils/sort.utils';

import {
  TicketFormDialogComponent,
  TicketFormResult,
  TicketModuleOption,
} from './ticket-form-dialog.component';
import {
  TicketActionsDialogComponent,
  TicketActionsResult,
  TicketAssignee,
} from './ticket-actions-dialog.component';

/** Buzón al que se avisa cada vez que se levanta un ticket. */
const SUPPORT_MAILBOX = 'mdt.epione@dohealth.com.mx';

/**
 * Módulos de respaldo para el select del formulario.
 *
 * El select se llena con la tabla `modules` (que es la que alimenta el
 * sidebar). Mientras esa tabla no esté sembrada, esta lista evita que el
 * usuario no pueda decir dónde falló. Es el mismo criterio que
 * `FALLBACK_MODULES` en `layout.component.ts`.
 */
const FALLBACK_MODULE_LABELS: ReadonlyArray<string> = [
  'Tablero',
  'Catálogos',
  'Intervenciones',
  'Inventario de productos',
  'Inventario de instrumental',
  'Inventario de equipo',
  'Reportes',
  'Módulos',
  'Permisos',
  'Roles',
  'Usuarios',
  'Soporte',
  'Otro / no aparece en la lista',
];

import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { SimpleExcelService } from '@shared/services/simple-excel.service';
import { TicketAssignDialogComponent, TicketAssignResult } from './ticket-assign-dialog.component';
@Component({
  selector: 'app-support-tickets',
  standalone: true,
  imports: [ SelectionBarComponent,PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, FormsModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './support-tickets.component.html',
  styleUrls: ['./support-tickets.component.scss'],
})
export class SupportTicketsComponent implements OnInit {
  /** Título = etiqueta visible del módulo (tabla `modules`). */
  readonly pageLabel = inject(ModulesService).moduleTitle('/support/tickets', 'Tickets de soporte');


  private ticketsService = inject(SupportTicketsService);
  private usersService   = inject(UsersService);
  private rolesService   = inject(RolesService);
  private modulesService = inject(ModulesService);
  private mailService    = inject(MailService);
  private message        = inject(CustomMessageService);
  private dialog         = inject(MatDialog);
  private router         = inject(Router);
  private simpleExcel    = inject(SimpleExcelService);

  /** Vista tabla / tarjetas y selección con checkbox (misma que en catálogos). */
  readonly listView = new ListView('support-tickets', 'table');
  readonly sel = new ListSelection<SupportTicket>(() => this.visibleTickets());

  /** Exporta a Excel los tickets seleccionados (o los visibles). */
  async exportExcel(rows: SupportTicket[] = this.visibleTickets()): Promise<void> {
    if (!rows.length) return;
    await this.simpleExcel.simpleXLSX({
      fileName: 'Tickets de soporte',
      sheets: [{
        sheetName: 'Tickets', header: [], lineBreak: 0,
        columns: [
          { columnKey: 'folio', columnName: 'Folio' },
          { columnKey: 'subject', columnName: 'Asunto' },
          { columnKey: 'location', columnName: 'Ubicación' },
          { columnKey: '_type', columnName: 'Tipo' },
          { columnKey: 'module', columnName: 'Módulo / herramienta' },
          { columnKey: 'requestedByName', columnName: 'Solicitante' },
          { columnKey: 'createdOn', columnName: 'Creado' },
          { columnKey: '_priority', columnName: 'Prioridad' },
          { columnKey: '_status', columnName: 'Estado' },
        ],
        data: rows.map((t: any) => ({ ...t, _type: t.ticketType === 'HARDWARE' ? 'Hardware' : 'Software', _priority: this.priorityName(t), _status: this.statusLabel(t.status) })),
      }],
    } as any);
  }

  readonly statuses = TICKET_STATUSES;

  // ─── Estado de la pantalla ─────────────────────────────────────────────
  readonly loading  = signal(false);
  readonly tickets  = signal<SupportTicket[]>([]);
  readonly filter   = signal<string>('');
  readonly statusFilter = signal<TicketStatus | 'TODOS'>('TODOS');

  /** Rol del usuario en sesión. Decide qué ve y qué puede hacer. */
  readonly userRole = signal<Role | null>(null);

  /**
   * Solo el Administrador Epione tiene acciones y ve los tickets de todos.
   * Los demás roles únicamente levantan tickets y ven los suyos.
   */
  readonly isAdmin = computed(() => isEpioneAdmin(this.userRole()));

  /** Opciones del select de módulo del formulario. */
  readonly moduleOptions = signal<TicketModuleOption[]>([]);

  /** Usuarios a los que el admin puede asignar un ticket. */
  readonly assignees = signal<TicketAssignee[]>([]);

  /** Columnas de la tabla. El admin ve dos más: solicitante y horas. */
  readonly displayedColumns = computed<string[]>(() =>
    this.isAdmin()
      ? ['select', 'folio', 'subject', 'module', 'requestedByName', 'createdOn', 'priority', 'estimatedHours', 'status', 'actions']
      : ['select', 'folio', 'subject', 'module', 'createdOn', 'priority', 'estimatedHours', 'status', 'actions']
  );

  /** Tickets ya filtrados por texto y por estado. */
  readonly visibleTickets = computed<SupportTicket[]>(() => {
    const term   = this.filter().trim().toLowerCase();
    const status = this.statusFilter();

    return this.tickets().filter(ticket => {
      if (status !== 'TODOS' && ticket.status !== status) return false;
      if (!term) return true;

      return [
        ticket.folio, ticket.subject, ticket.description,
        ticket.module, ticket.location, ticket.requestedByName,
        ticket.assignedToName,
      ].some(field => (field ?? '').toString().toLowerCase().includes(term));
    });
  });

  /** Filtro de estado activo, como chip debajo de la búsqueda. */
  readonly activeChips = computed<FilterChip[]>(() => {
    const st = this.statusFilter();
    if (st === 'TODOS') return [];
    return [{ key: 'status', label: 'Estado', value: TICKET_STATUSES.find(s => s.id === st)?.name ?? st }];
  });

  /** Conteo por estado, para los chips del encabezado. */
  readonly counters = computed(() => {
    const all = this.tickets();
    return {
      total:       all.length,
      pendiente:   all.filter(t => t.status === 'PENDIENTE').length,
      asignado:    all.filter(t => t.status === 'ASIGNADO').length,
      solucionado: all.filter(t => t.status === 'SOLUCIONADO').length,
    };
  });

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['supportTickets'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadTickets()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.loadUserAndRole();
    await Promise.all([
      this.loadTickets(),
      this.loadModules(),
      this.isAdmin() ? this.loadAssignees() : Promise.resolve(),
    ]);
  }

  // ─── Carga de datos ────────────────────────────────────────────────────

  private async loadUserAndRole(): Promise<void> {
    if (!this.usersService.currentUser()) {
      try {
        await this.usersService.loadCurrentUser();
      } catch (err) {
        console.warn('[Soporte] No se pudo cargar el usuario:', err);
      }
    }

    const user = this.usersService.currentUser();
    if (!user?.role) {
      this.userRole.set(null);
      return;
    }

    try {
      await this.rolesService.search({ searchType: 'default', condition: { id: user.role } });
      const roles = this.rolesService.listData();
      this.userRole.set(roles.length > 0 ? (roles[0] as Role) : null);
    } catch (err) {
      console.warn('[Soporte] No se pudo cargar el rol:', err);
      this.userRole.set(null);
    }
  }

  async loadTickets(): Promise<void> {
    // El filtro por usuario va en el `condition` del search: así el rol no
    // administrador nunca recibe tickets ajenos, ni siquiera en la red.
    const isAdmin = this.isAdmin();
    const ownerId = this.usersService.currentUser()?.id ?? null;

    // Fail-closed: si no somos admin y no se pudo resolver el usuario (una
    // falla de red al cargarlo, por ejemplo), NO se consulta. Pedir la lista
    // sin filtro traería la bandeja completa de la organización.
    if (!isAdmin && !ownerId) {
      this.tickets.set([]);
      await this.message.open({
        title: 'Aviso', type: 'warning', dismissBtn: true,
        message: 'No se pudo identificar tu usuario, así que no se muestran tickets. Vuelve a entrar a la pantalla o recarga la sesión.',
      });
      return;
    }

    this.loading.set(true);
    try {
      const data = await this.ticketsService.list(isAdmin ? null : ownerId);
      this.tickets.set(this.sortByDateDesc(data));
    } catch (err: any) {
      console.error('[Soporte] Error cargando tickets:', err);
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudieron cargar los tickets. ${err?.message ?? ''}`,
      });
    } finally {
      this.loading.set(false);
    }
  }

  private async loadModules(): Promise<void> {
    let options: TicketModuleOption[] = [];

    try {
      await this.modulesService.search({
        searchType: 'default',
        condition: { deleted: false, active: true },
      });
      // Solo los módulos que el ROL del usuario tiene asignados en su
      // navAccessTree (los mismos que ve en el menú); si el rol no tiene
      // árbol configurado, se muestran todos como respaldo.
      const navAccessTree = this.userRole()?.navAccessTree;
      options = (this.modulesService.listData() ?? [])
        .filter(m => !!(m.label || m.name))
        .filter(m => !navAccessTree?.length || (m.id != null && navAccessTree.includes(m.id)))
        .map(m => ({ id: m.id ?? null, label: m.label || m.name || '' }));
    } catch (err) {
      console.warn('[Soporte] No se pudieron cargar los módulos:', err);
    }

    if (options.length === 0) {
      options = FALLBACK_MODULE_LABELS.map(label => ({ id: null, label }));
    } else {
      // Siempre debe existir una salida para lo que no encaja en un módulo.
      options = sortAlphabetically(options, 'label');
      options.push({ id: null, label: 'Otro / no aparece en la lista' });
    }

    this.moduleOptions.set(options);
  }

  /** Etiqueta del tiempo estimado (= tiempo de resolución de la prioridad). */
  estimatedLabel(ticket: SupportTicket): string {
    const p = ticketPriority(ticket.priority);
    if (p) return p.resolution;
    return ticket.estimatedHours != null ? `${ticket.estimatedHours} h` : 'Sin estimar';
  }

  priorityName(ticket: SupportTicket): string {
    return ticketPriority(ticket.priority)?.name ?? '—';
  }

  priorityClass(ticket: SupportTicket): string {
    return 'priority-badge priority-' + String(ticket.priority ?? 'none').toLowerCase();
  }

  /** Solo se puede asignar a usuarios con rol Administrador Epione. */
  private async loadAssignees(): Promise<void> {
    try {
      const rolesResp: any = await this.rolesService.search({ searchType: 'default', condition: { deleted: false } });
      const adminRoleIds = new Set<string>(
        ((rolesResp?.data ?? []) as Role[]).filter(r => isEpioneAdmin(r)).map(r => String(r.id))
      );
      await this.usersService.search({ searchType: 'default', condition: { deleted: false } });
      const people = (this.usersService.listData() ?? [])
        .filter(u => !!u.id)
        .filter(u => adminRoleIds.has(String((u as any).role ?? '')))
        .map(u => ({ id: u.id!, name: u.name || u.email || 'Sin nombre' }));
      this.assignees.set(sortAlphabetically(people, 'name'));
    } catch (err) {
      console.warn('[Soporte] No se pudieron cargar los usuarios asignables:', err);
      this.assignees.set([]);
    }
  }

  // ─── Alta de ticket ────────────────────────────────────────────────────

  /** Quien levantó el ticket puede editarlo mientras no esté solucionado. */
  canEdit(ticket: SupportTicket): boolean {
    const me = this.usersService.currentUser()?.id;
    return !!me && ticket.requestedBy === me && ticket.status !== 'SOLUCIONADO';
  }

  openEdit(ticket: SupportTicket): void {
    if (!this.canEdit(ticket)) return;
    const ref = this.dialog.open(TicketFormDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        modules: this.moduleOptions(),
        createdOn: ticket.createdOn ?? '',
        requestedByName: ticket.requestedByName || 'Usuario',
        ticket,
      },
    });
    ref.afterClosed().subscribe(async (result: TicketFormResult | undefined) => {
      if (!result) return;
      this.message.open({ title: 'Espere', type: 'success', loading: true, message: 'Guardando el ticket' });
      try {
        await this.ticketsService.updateTicket({
          id:          ticket.id,
          ticketType:  result.ticketType,
          priority:    result.priority,
          subject:     result.subject,
          description: result.description,
          moduleId:    result.moduleId,
          module:      result.module,
          location:    result.location || null,
        });
        this.message.close();
        await this.loadTickets();
        await this.message.open({ title: '¡Hecho!', type: 'success', duration: 2000, message: `Ticket ${ticket.folio} actualizado.` });
      } catch (err: any) {
        this.message.close();
        await this.message.open({ title: 'Error', type: 'error', dismissBtn: true, message: `No se pudo guardar el ticket. ${err?.message ?? ''}` });
      }
    });
  }

  openCreate(): void {
    const user = this.usersService.currentUser();

    const ref = this.dialog.open(TicketFormDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        modules: this.moduleOptions(),
        createdOn: new Date().toLocaleString('es-MX'),
        requestedByName: user?.name || user?.email || 'Usuario',
      },
    });

    ref.afterClosed().subscribe(async (result: TicketFormResult | undefined) => {
      if (!result) return;
      await this.createTicket(result);
    });
  }

  private async createTicket(form: TicketFormResult): Promise<void> {
    const user = this.usersService.currentUser();

    this.message.open({
      title: 'Espere', type: 'success', loading: true,
      message: 'Guardando el ticket',
    });

    let created: SupportTicket | null = null;
    try {
      created = await this.ticketsService.createTicket({
        ticketType:       form.ticketType,
        priority:         form.priority,
        subject:          form.subject,
        description:      form.description,
        moduleId:         form.moduleId,
        module:           form.module,
        location:         form.location || null,
        requestedBy:      user?.id ?? null,
        requestedByName:  user?.name ?? null,
        requestedByEmail: user?.email ?? null,
      });
    } catch (err: any) {
      this.message.close();
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudo crear el ticket. ${err?.message ?? ''}`,
      });
      return;
    }

    this.message.close();

    if (!created) {
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: 'El servidor no devolvió el ticket creado.',
      });
      return;
    }

    await this.loadTickets();

    // El correo va DESPUÉS de guardar y su fallo no invalida el ticket: si el
    // SMTP está caído el registro ya existe y solo se avisa que no salió.
    const mailSent = await this.notifyByMail(created);

    await this.message.open({
      title: '¡Hecho!',
      type: mailSent ? 'success' : 'warning',
      dismissBtn: true,
      message: mailSent
        ? `Ticket <b>${created.folio}</b> creado. Se envió el aviso a ${SUPPORT_MAILBOX}.`
        : `Ticket <b>${created.folio}</b> creado, pero <b>no se pudo enviar el correo</b> a ${SUPPORT_MAILBOX}. El ticket ya quedó registrado.`,
    });
  }

  /**
   * Avisa a la mesa de servicio. Usa el `useCase: 'SOPORTE'` del endpoint
   * `send-mail`, que arma la plantilla con los datos del ticket.
   */
  private async notifyByMail(ticket: SupportTicket): Promise<boolean> {
    try {
      const response = await this.mailService.sendMail({
        // Los destinatarios los decide el API (ya no se mandan desde aquí).
        useCase:  'SOPORTE',
        // Campos genéricos del endpoint; la plantilla de SOPORTE usa `ticket`.
        title:       ticket.folio,
        description: ticket.subject,
        tool:        ticket.module,
        priority:    ticketPriority(ticket.priority)?.name ?? '',
        category:    ticket.location ?? '',
        ticket: {
          folio:            ticket.folio,
          subject:          ticket.subject,
          description:      ticket.description,
          module:           ticket.module,
          location:         ticket.location,
          status:           ticket.status,
          priority:         ticketPriority(ticket.priority)?.name ?? null,
          estimated:        ticketPriority(ticket.priority)?.resolution ?? null,
          createdOn:        ticket.createdOn,
          requestedByName:  ticket.requestedByName,
          requestedByEmail: ticket.requestedByEmail,
        },
      });

      // El backend devuelve {sent: boolean} dentro de `data`.
      const payload = Array.isArray(response?.data) ? response.data[0] : response?.data;
      return payload?.sent !== false;
    } catch (err) {
      console.error('[Soporte] No se pudo enviar el correo del ticket:', err);
      return false;
    }
  }

  // ─── Acciones del administrador ────────────────────────────────────────

  openActions(ticket: SupportTicket): void {
    if (!this.isAdmin()) return;

    const ref = this.dialog.open(TicketActionsDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { ticket, assignees: this.assignees() },
    });

    ref.afterClosed().subscribe(async (result: TicketFormResult | undefined) => {
      if (!result) return;
      await this.saveOwnEdit(ticket, result);
    });
  }

  /** El creador guarda los datos de SU ticket (no las acciones del admin). */
  private async saveOwnEdit(ticket: SupportTicket, form: TicketFormResult): Promise<void> {
    this.message.open({ title: 'Espere', type: 'success', loading: true, message: 'Guardando el ticket' });
    try {
      await this.ticketsService.updateTicket({
        id:          ticket.id,
        ticketType:  form.ticketType,
        priority:    form.priority,
        subject:     form.subject,
        description: form.description,
        moduleId:    form.moduleId,
        module:      form.module,
        location:    form.location || null,
      } as any);
      this.message.close();
      await this.loadTickets();
      await this.message.open({ title: '¡Hecho!', type: 'success', duration: 2000, message: `Ticket ${ticket.folio} actualizado.` });
    } catch (err: any) {
      this.message.close();
      await this.message.open({ title: 'Error', type: 'error', dismissBtn: true, message: `No se pudo guardar el ticket. ${err?.message ?? ''}` });
    }
  }

  /** Asignar responsable: solo el Administrador Epione y solo a usuarios con ese rol. */
  openAssign(ticket: SupportTicket): void {
    if (!this.isAdmin()) return;
    const ref = this.dialog.open(TicketAssignDialogComponent, {
      width: '480px', maxWidth: '95vw', autoFocus: false,
      data: { folio: ticket.folio ?? '', subject: ticket.subject ?? '', assignedTo: ticket.assignedTo ?? null, assignees: this.assignees() },
    });
    ref.afterClosed().subscribe(async (result: TicketAssignResult | undefined) => {
      if (!result) return;
      try {
        // El API valida el rol del responsable, pone su nombre y el estado.
        await this.ticketsService.updateTicket({ id: ticket.id, assignedTo: result.assignedTo } as any);
        await this.loadTickets();
        await this.message.open({
          title: '¡Hecho!', type: 'success', duration: 2000,
          message: result.assignedTo ? `Responsable asignado a ${ticket.folio}. Se le envía un aviso a su correo.` : `Se quitó el responsable de ${ticket.folio}.`,
        });
      } catch (err: any) {
        await this.message.open({ title: 'Error', type: 'error', dismissBtn: true, message: `No se pudo asignar. ${err?.message ?? ''}` });
      }
    });
  }

  private async saveActions(ticket: SupportTicket, result: TicketActionsResult): Promise<void> {
    this.message.open({
      title: 'Espere', type: 'success', loading: true,
      message: 'Guardando las acciones del ticket',
    });

    try {
      await this.ticketsService.updateTicket({
        id:             ticket.id,
        status:         result.status,
        priority:       result.priority,
        estimatedHours: result.estimatedHours,
        assignedTo:     result.assignedTo,
        assignedToName: result.assignedToName,
        resolution:     result.resolution,
        // `resolvedOn` NO se manda: lo sella el hook `before_update` de
        // `supportTickets` en el API, para que la regla viva en un solo lado
        // y valga también si alguien pega al endpoint directo.
      });

      this.message.close();
      await this.loadTickets();
      await this.message.open({
        title: '¡Hecho!', type: 'success', duration: 2000,
        message: `Ticket ${ticket.folio} actualizado.`,
      });
    } catch (err: any) {
      this.message.close();
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudieron guardar las acciones. ${err?.message ?? ''}`,
      });
    }
  }

  /** Cambio rápido de estado desde el menú de la fila (solo admin). */
  async changeStatus(ticket: SupportTicket, status: TicketStatus): Promise<void> {
    if (!this.isAdmin() || ticket.status === status) return;

    try {
      await this.ticketsService.changeStatus(ticket.id, status);
      await this.loadTickets();
    } catch (err: any) {
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudo cambiar el estado. ${err?.message ?? ''}`,
      });
    }
  }

  async deleteTicket(ticket: SupportTicket): Promise<void> {
    if (!this.isAdmin()) return;

    const confirmed = await this.message.open({
      title: 'Confirmar',
      type: 'warning',
      message: `¿Eliminar el ticket <b>${ticket.folio}</b>? Se conserva en la base como borrado lógico.`,
      acceptBtn: true,
      dismissBtn: true,
    });
    if (!confirmed) return;

    try {
      await this.ticketsService.softDeleteTicket(ticket.id);
      await this.loadTickets();
    } catch (err: any) {
      await this.message.open({
        title: 'Error', type: 'error', dismissBtn: true,
        message: `No se pudo eliminar el ticket. ${err?.message ?? ''}`,
      });
    }
  }

  /** Detalle en solo lectura, para cualquier rol. */
  async openDetail(ticket: SupportTicket): Promise<void> {
    await this.message.open({
      title: `Ticket ${ticket.folio}`,
      type: 'neutral',
      dismissBtn: true,
      message: `
        <b>Asunto:</b> ${ticket.subject}<br>
        <b>Tipo:</b> ${ticket.ticketType === 'HARDWARE' ? 'Hardware' : 'Software'}<br>
        <b>${ticket.ticketType === 'HARDWARE' ? 'Herramienta' : 'Módulo'}:</b> ${ticket.module}<br>
        <b>¿En qué parte?:</b> ${ticket.location || 'No especificado'}<br>
        <b>Fecha de creación:</b> ${ticket.createdOn ?? ''}<br>
        <b>Estado:</b> ${this.statusLabel(ticket.status)}<br>
        <b>Prioridad:</b> ${ticketPriority(ticket.priority)?.name ?? 'Sin prioridad'}<br>
        <b>Tiempo estimado (resolución):</b> ${this.estimatedLabel(ticket)}<br>
        <b>Asignado a:</b> ${ticket.assignedToName || 'Sin asignar'}<br>
        <br>
        <b>Descripción:</b><br>${(ticket.description ?? '').replace(/\n/g, '<br>')}
        ${ticket.resolution ? `<br><br><b>Notas de resolución:</b><br>${ticket.resolution.replace(/\n/g, '<br>')}` : ''}
      `,
    });
  }

  // ─── Helpers de vista ──────────────────────────────────────────────────

  statusLabel(status: TicketStatus): string {
    return TICKET_STATUSES.find(s => s.id === status)?.name ?? status;
  }

  /** Clase del badge de estado (mismas clases de color que el resto). */
  statusClass(status: TicketStatus): string {
    const color = TICKET_STATUSES.find(s => s.id === status)?.color ?? 'blue';
    return `${color}-status`;
  }

  applyFilter(event: Event): void {
    this.filter.set((event.target as HTMLInputElement).value ?? '');
  }

  setStatusFilter(status: TicketStatus | 'TODOS'): void {
    this.statusFilter.set(status);
  }

  goBack(): void {
    this.router.navigate(['/support/menu']);
  }

  /**
   * Ordena por fecha de creación, más reciente primero.
   *
   * `createdOn` se guarda como texto con formato "MM/DD/YYYY, HH:MM:SS"
   * (ver `epione_time.py`), que `Date` de JS interpreta bien. Si algún
   * registro trae otro formato, se manda al final en vez de romper el orden.
   */
  private sortByDateDesc(tickets: SupportTicket[]): SupportTicket[] {
    return [...tickets].sort((a, b) => {
      const timeA = Date.parse(a.createdOn ?? '');
      const timeB = Date.parse(b.createdOn ?? '');
      if (isNaN(timeA) && isNaN(timeB)) return 0;
      if (isNaN(timeA)) return 1;
      if (isNaN(timeB)) return -1;
      return timeB - timeA;
    });
  }
}
